import { useState, useEffect, useCallback } from 'react';
import { MARKET_REFRESH_MS, friendsAPI, leaderboardAPI } from '../services/api';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import Toast from '../components/Toast';
import { formatNumber } from '../utils/format';
import { getBadge, BadgePill, RankDisplay, LBAvatar, LBHeader, LBRow, LBCard } from '../components/LeaderboardRow';

export default function LeaderboardPage() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [toast, setToast]         = useState(null);
  const [friendIds, setFriendIds] = useState(() => new Set());
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const [isMobile, setIsMobile]   = useState(window.innerWidth < 768);
  const [activeTab, setActiveTab] = useState('current'); // 'current' | 'history'
  const [historyYear, setHistoryYear] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyYears, setHistoryYears] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const { user } = useAuth();

  const fetchCurrentLeaderboard = useCallback(() => {
    Promise.all([leaderboardAPI.get(), friendsAPI.list(), friendsAPI.sent()])
      .then(([leaderboardRes, friendsRes, sentRes]) => {
        setData(leaderboardRes.data);
        setFriendIds(new Set((friendsRes.data || []).map(f => f.friend_id)));
        setPendingIds(new Set((sentRes.data || []).map(r => r.id)));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    fetchCurrentLeaderboard();
    const interval = setInterval(fetchCurrentLeaderboard, MARKET_REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchCurrentLeaderboard]);

  // Load history years when switching to history tab
  useEffect(() => {
    if (activeTab !== 'history') return;
    if (historyYears.length) return; // already loaded
    const currentYear = new Date().getFullYear();
    API.get(`/leaderboard/history/${currentYear - 1}`)
      .then(r => {
        const years = r.data.availableYears || [];
        setHistoryYears(years);
        if (years.length && !historyYear) setHistoryYear(years[0]);
      })
      .catch(() => setHistoryYears([]));
  }, [activeTab]); // eslint-disable-line

  // Load history for selected year
  useEffect(() => {
    if (!historyYear || activeTab !== 'history') return;
    setHistoryLoading(true);
    API.get(`/leaderboard/history/${historyYear}`)
      .then(r => setHistoryData(r.data.rows || []))
      .catch(() => setHistoryData([]))
      .finally(() => setHistoryLoading(false));
  }, [historyYear, activeTab]);

  const sendRequest = async (userId) => {
    try {
      await friendsAPI.sendRequest(userId);
      setPendingIds(prev => new Set([...prev, userId]));
      setToast({ msg: 'Friend request sent!', type: 'success' });
    } catch (err) {
      setToast({ msg: err.response?.data?.error || 'Failed to send request', type: 'error' });
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!data)   return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Failed to load</div>;

  const { top50, me, myRankInTop, totalUsers } = data;
  const top3 = top50.slice(0, 3);
  const RowComp = isMobile ? LBCard : LBRow;

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onDismiss={() => setToast(null)} />}

      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 className="page-title">Leaderboard</h1>
          <p className="page-subtitle">
            Top 50 · {formatNumber(totalUsers)} total players
            {me && !myRankInTop && <span style={{ marginLeft: 8, color: 'var(--accent)', fontWeight: 600 }}>· Your rank: #{me.rank}</span>}
          </p>
        </div>
        {me && (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 16px', textAlign: 'center', minWidth: 110 }}>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Your rank</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', fontWeight: 700, color: 'var(--accent)', lineHeight: 1.1 }}>#{me.rank}</div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>of {formatNumber(totalUsers)}</div>
            {getBadge(me.rank) && <div style={{ marginTop: 4 }}><BadgePill rank={me.rank} /></div>}
          </div>
        )}
      </div>

      {/* Tabs: Current Season / History */}
      <div style={{ display: 'flex', gap: 6, marginBottom: '1rem', borderBottom: '1px solid var(--border)' }}>
        {[['current', 'IPL 2026'], ['history', 'Past Seasons']].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)} style={{
            padding: '8px 16px', border: 'none', cursor: 'pointer', background: 'none',
            color: activeTab === key ? 'var(--accent)' : 'var(--text-muted)',
            borderBottom: activeTab === key ? '2px solid var(--accent)' : '2px solid transparent',
            fontSize: '0.82rem', fontWeight: 600, transition: 'color 0.15s', marginBottom: -1,
          }}>{label}</button>
        ))}
      </div>

      {activeTab === 'history' ? (
        /* ── Past seasons ── */
        <div>
          {historyYears.length === 0 && !historyLoading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              No historical leaderboard data yet.<br />
              <span style={{ fontSize: '0.78rem' }}>Data will appear here after each season reset.</span>
            </div>
          ) : (
            <>
              {historyYears.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '1rem' }}>
                  {historyYears.map(yr => (
                    <button key={yr} onClick={() => setHistoryYear(yr)} style={{
                      padding: '5px 14px', borderRadius: 20, border: '1px solid',
                      borderColor: historyYear === yr ? 'var(--accent)' : 'var(--border)',
                      background: historyYear === yr ? 'rgba(245,158,11,0.12)' : 'transparent',
                      color: historyYear === yr ? 'var(--accent)' : 'var(--text-muted)',
                      fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                    }}>IPL {yr}</button>
                  ))}
                </div>
              )}
              {historyLoading && <div className="loading-screen"><div className="spinner" /></div>}
              {!historyLoading && historyData && (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  {historyData.length === 0
                    ? <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>No data for IPL {historyYear}</div>
                    : historyData.map((entry, i) => (
                      <div key={entry.user_id || i} style={{
                        display: isMobile ? 'flex' : 'grid',
                        gridTemplateColumns: isMobile ? undefined : '44px minmax(0,1fr) minmax(70px,86px)',
                        flexDirection: isMobile ? 'row' : undefined,
                        alignItems: 'center', gap: 8, padding: '10px 1.25rem',
                        borderBottom: '1px solid var(--border)',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'center', width: isMobile ? 28 : undefined }}>
                          <RankDisplay rank={entry.rank} />
                        </div>
                        {isMobile && <LBAvatar name={entry.display_name || entry.username} size={32} />}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, fontSize: '0.87rem' }}>{entry.display_name || entry.username}</div>
                          {!isMobile && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{entry.holdings_count || 0} stocks</div>}
                        </div>
                        <div style={{ textAlign: 'right', fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--accent)', fontSize: '0.95rem', flexShrink: 0 }}>
                          {parseFloat(entry.total_value || 0).toFixed(0)}<span style={{ fontSize: '0.62rem', color: 'var(--text-muted)', marginLeft: 2 }}>pts</span>
                        </div>
                      </div>
                    ))
                  }
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        /* ── Current season ── */
        <>
          {/* Badge legend */}
          <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
            {[
              { range: 'Top 100', badge: getBadge(1) },
              { range: '101–500', badge: getBadge(200) },
              { range: '501–1000', badge: getBadge(600) },
            ].map(({ range, badge }) => badge && (
              <div key={range} style={{ display: 'flex', alignItems: 'center', gap: 5, background: badge.bg, border: `1px solid ${badge.border}`, borderRadius: 20, padding: '3px 10px', fontSize: '0.72rem', color: badge.color, fontWeight: 600 }}>
                {badge.emoji} {badge.label} <span style={{ opacity: 0.7 }}>· {range}</span>
              </div>
            ))}
          </div>

          {/* Top 3 podium */}
          {top3.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(3, 190px)', justifyContent: 'start', gap: isMobile ? 8 : 14, margin: '0 0 1.1rem', width: isMobile ? '100%' : 'fit-content' }}>
              {[top3[1], top3[0], top3[2]].filter(Boolean).map(entry => {
                const rank  = parseInt(entry.rank);
                const isMe  = entry.id === user?.id || entry.username === user?.username;
                const color = rank === 1 ? '#fbbf24' : rank === 2 ? '#94a3b8' : '#cd7c2f';
                return (
                  <div key={entry.id} className="card" style={{ textAlign: 'center', order: rank === 1 ? -1 : rank, border: isMe ? '1px solid var(--accent)' : `1px solid ${rank === 1 ? 'rgba(251,191,36,0.35)' : 'var(--border)'}`, background: rank === 1 ? 'linear-gradient(180deg, rgba(251,191,36,0.08), var(--bg-elevated))' : 'var(--bg-card)', padding: isMobile ? '0.7rem 0.55rem' : '0.7rem 0.65rem', borderRadius: 16, minHeight: isMobile ? 148 : 128, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: isMobile ? 4 : 3 }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
                      <LBAvatar name={entry.display_name || entry.username} size={isMobile ? 32 : rank === 1 ? 38 : 34} />
                    </div>
                    <div style={{ fontSize: isMobile ? '0.68rem' : '0.62rem', lineHeight: 1, marginBottom: 1, color, fontWeight: 700, letterSpacing: '0.08em' }}>
                      {rank === 1 ? 'TOP' : rank === 2 ? 'RUN' : '3RD'}
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: isMobile ? '0.82rem' : '0.76rem', color, lineHeight: 1 }}>#{rank}</div>
                    <div style={{ width: '100%', margin: '2px 0 1px' }}>
                      <div className="truncate-1" style={{ maxWidth: '100%', fontWeight: 600, fontSize: isMobile ? '0.78rem' : '0.76rem' }}>{entry.display_name || entry.username}</div>
                      {isMe && <span style={{ color: 'var(--accent)', fontSize: '0.63rem', display: 'block', marginTop: 1 }}>you</span>}
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? (rank === 1 ? '1.05rem' : '0.98rem') : (rank === 1 ? '0.96rem' : '0.9rem'), fontWeight: 700, lineHeight: 1 }}>
                      {parseFloat(entry.total_value).toFixed(0)}<span style={{ fontSize: '0.58rem', color: 'var(--text-muted)', marginLeft: 2 }}>pts</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Full table */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {!isMobile && <LBHeader showAdd />}

            {top50.length === 0 ? (
              <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>No traders yet — be the first!</div>
            ) : (
              top50.map(entry => (
                <RowComp
                  key={entry.id}
                  entry={entry}
                  isMe={entry.id === user?.id || entry.username === user?.username}
                  requestState={friendIds.has(entry.id) ? 'friends' : pendingIds.has(entry.id) ? 'sent' : 'idle'}
                  onSendRequest={sendRequest}
                  showAddCol
                />
              ))
            )}

            {me && !myRankInTop && (
              <>
                <div style={{ padding: '8px 1.25rem', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', fontSize: '0.7rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  <span>{parseInt(me.rank) - 50} traders below top 50</span>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                </div>
                <RowComp entry={me} isMe requestState="friends" showAddCol />
              </>
            )}
          </div>

          <div style={{ marginTop: 12, fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.8 }}>
            <span style={{ display: 'block' }}>Rankings update every price change</span>
            <span style={{ display: 'block' }}>Badges: 🥇 Top 100 · 🥈 Top 500 · 🥉 Top 1000</span>
          </div>
        </>
      )}
    </div>
  );
}
